import React from 'react';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { postScheduledDeployment } from 'Services';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import styles from 'Components/NotificationFactory/FactoryModals/FactoryModals.css';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { RQ_SCHEDULED_DEPLOYMENT } from '@brighterion/utils/react-query-constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { timeZone } from '@brighterion/utils/date-timezone';
import type { ReactQueryError } from '@brighterion/types/react-query';
import type { ScheduledDeploymentSchema } from '@brighterion/types/scheduled-deployment';
import type { ScheduledDeployment } from './useGetScheduledDeployment';

const usePostScheduledDeployment = () => {
    const queryClient = useQueryClient();
    const { addModal, addTechnicalErrorModal } = useReduxModal();

    const handleOnSuccess = (data: ScheduledDeployment, variables: ScheduledDeploymentSchema) => {
        const { locale } = useTemplateStore.getState().template;
        const { scheduledTime, totalUpdates } = data;
        const getDate = new Date(scheduledTime).toLocaleString(locale, {
            timeZone,
            dateStyle: 'full',
            timeStyle: 'short'
        });
        let modalTitle;
        let modalBody;

        if (variables.deployNow) {
            modalTitle = t('deploymentStartedTitle');
            modalBody = (
                <Trans i18nKey='ScheduledDeploymentStartedContent'>
                    Your scheduled deployment of <strong>{{ totalUpdates }} updates</strong> has started and it may take
                    a while to complete. You will be notified when the deployment is live.
                    <br />
                </Trans>
            );
        } else {
            modalTitle = t('deploymentScheduledTitle');
            modalBody = (
                <Trans i18nKey='ScheduledDeploymentSuccessContent'>
                    Your deployment of <strong>{{ totalUpdates }} updates</strong> has been scheduled to deploy at{' '}
                    <strong>{{ getDate }}</strong>.<br />
                    <br />
                    Any changes made to the sandbox will not be deployed until a new deployment is scheduled or
                    deployed.
                </Trans>
            );
        }
        addModal({
            id: 'scheduled-deployment-success',
            status: 'success',
            title: modalTitle,
            content: (
                <div className={styles.body}>
                    <p>{modalBody}</p>
                    <p>{t('Close this message to return to the sandbox')}</p>
                </div>
            ),
            cancelText: t('Close')
        });
    };

    const handleOnError = (error: ReactQueryError) => {
        if (error.status === 409) {
            addModal({
                id: 'scheduled-deployment-warning',
                status: 'warning',
                title: t('Deployment is in progress'),
                content: t('A deployment is currently in progress'),
                cancelText: t('Close')
            });
        } else {
            addTechnicalErrorModal(t('Failed to make deployment'), 'Unable to deploy changes');
        }
    };

    return useMutation(postScheduledDeployment, {
        onSuccess: (data, variables) => {
            handleOnSuccess(data, variables);
            queryClient.invalidateQueries([RQ_SCHEDULED_DEPLOYMENT]);
        },
        onError: (error: ReactQueryError) => {
            handleOnError(error);
        }
    });
};

export default usePostScheduledDeployment;
